import { useEffect, useState } from "react"
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { producaoApi, errorMessage, type Absence, type KoortexTeam, type Technician, type Van } from "./api"
import { useProducao } from "./context"
import { VanPill } from "./components"
import { selectClass } from "./AssignPanel"
import { PairDialog, PhoneCell, useDevices } from "./Telemoveis"
import { useTrelloOptions, type TrelloOption } from "./TrelloConfig"
import { addDays, fmtShort, jobDays, mondayOf, todayIso, TRELLO_COLORS } from "./lib"
import { confirmDialog } from "@/lib/confirm"


/**
 * G5 · Equipas e carrinhas, com as ausências numa lista simples (o G3 precisa
 * delas para a disponibilidade; o G6 completo fica para a Fase 4) e a
 * configuração do Trello (em Produção → Configurações).
 */
export function Equipas() {
  const { board, loading, reload, shell } = useProducao()
  const [editVan, setEditVan] = useState<Partial<Van> | null>(null)
  const [editTech, setEditTech] = useState<Partial<Technician> | null>(null)
  const trello = useTrelloOptions()
  const [pairFor, setPairFor] = useState<Technician | null>(null)
  const { devices, reload: reloadDevices } = useDevices()
  // A equipa do Koortex da carrinha só conta (e só se grava) com as obras sobre os contratos (M5).
  const hostMode = board?.config.contracts_backend === "host"
  const teams = useKoortexTeams(hostMode)

  if (loading || !board) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin" /></div>

  const monday = mondayOf(todayIso())
  const weekEnd = addDays(monday, 5)
  const jobsThisWeek = (vanId: string) =>
    board.jobs.filter((j) => j.van_id === vanId && j.status !== "cancelada" && jobDays(j).some((d) => d >= monday && d <= weekEnd)).length

  const techName = (id: string | null) => board.technicians.find((t) => t.id === id)?.name ?? "—"
  const labelName = (id: string | null) => (id ? trello.labels.find((l) => l.id === id)?.name || id : "—")
  const memberName = (id: string | null) => (id ? trello.members.find((m) => m.id === id)?.name || id : "—")
  const teamName = (id: Van["team_id"]) => (id != null && String(id) !== "" ? teams.find((t) => t.id === Number(id))?.name ?? `#${id}` : "—")

  const remove = async (kind: "van" | "tech", id: string) => {
    if (!shell || !(await confirmDialog("Apagar?", { destructive: true, confirmLabel: "Apagar" }))) return
    try {
      await (kind === "van" ? producaoApi.deleteVan(shell, id) : producaoApi.deleteTechnician(shell, id))
      await reload()
    } catch (err) {
      shell.bridge.toast({ variant: "error", title: "Não foi possível apagar", description: errorMessage(err) })
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <section className="space-y-3">
        <SectionHeader title="Carrinhas" sub="A cor é da equipa e espelha a etiqueta no Trello (o Trello tem 10 cores)." onAdd={() => setEditVan({ active: true, color: TRELLO_COLORS[5]!.hex })} addLabel="Nova carrinha" />
        <Table head={["Carrinha", "Armazém no Odoo", "Chefe fixo", ...(hostMode ? ["Equipa no Koortex"] : []), "Etiqueta no Trello", "Obras esta semana", ""]}>
          {board.vans.map((v) => (
            <tr key={v.id} className={cn("border-b last:border-0 border-border", !v.active && "opacity-50")}>
              <td className="px-3 py-2"><VanPill van={v} className="text-sm" />{!v.active && <span className="ml-2 text-xs">(inativa)</span>}</td>
              <td className="px-3 py-2">{v.odoo_location ?? "—"}</td>
              <td className="px-3 py-2">{techName(v.technician_id)}</td>
              {hostMode && <td className="px-3 py-2">{teamName(v.team_id)}</td>}
              <td className="px-3 py-2">{labelName(v.trello_label_id)}</td>
              <td className="px-3 py-2">{jobsThisWeek(v.id)}</td>
              <RowActions onEdit={() => setEditVan(v)} onDelete={() => remove("van", v.id)} />
            </tr>
          ))}
          {board.vans.length === 0 && <EmptyRow cols={hostMode ? 7 : 6} text="Ainda sem carrinhas." />}
        </Table>
      </section>

      <section className="space-y-3">
        <SectionHeader
          title="Técnicos e armazém"
          sub="Cada pessoa usa um telemóvel da empresa, emparelhado aqui (sem conta Koortex). Os técnicos têm a carrinha e as obras; as pessoas do armazém têm a Ronda de stock."
          onAdd={() => setEditTech({ active: true, is_lead: false, kind: "tecnico", can_manage_stock: false })}
          addLabel="Nova pessoa"
        />
        <Table head={["Nome", "Papel", "Carrinha habitual", "Membro no Trello", "Telemóvel", ""]}>
          {board.technicians.map((t) => (
            <tr key={t.id} className={cn("border-b last:border-0 border-border", !t.active && "opacity-50")}>
              <td className="px-3 py-2 font-medium">{t.name}{!t.active && <span className="ml-2 text-xs font-normal">(inativo)</span>}</td>
              <td className="px-3 py-2">{roleLabel(t)}</td>
              <td className="px-3 py-2">{board.vans.find((v) => v.id === t.van_id)?.name ?? "—"}</td>
              <td className="px-3 py-2">{memberName(t.trello_member_id)}</td>
              <td className="px-3 py-2">
                <PhoneCell tech={t} devices={devices} onPair={() => setPairFor(t)} onChanged={reloadDevices} />
              </td>
              <RowActions onEdit={() => setEditTech(t)} onDelete={() => remove("tech", t.id)} />
            </tr>
          ))}
          {board.technicians.length === 0 && <EmptyRow cols={6} text="Ainda sem técnicos." />}
        </Table>
      </section>

      <Absences absences={board.absences} technicians={board.technicians} />


      {editVan && (
        <VanDialog van={editVan} technicians={board.technicians} labels={trello.labels} teams={hostMode ? teams : null} onClose={() => setEditVan(null)} />
      )}
      {pairFor && (
        <PairDialog
          tech={pairFor}
          onClose={() => {
            setPairFor(null)
            void reloadDevices()
          }}
        />
      )}
      {editTech && (
        <TechDialog tech={editTech} vans={board.vans} members={trello.members} onClose={() => setEditTech(null)} />
      )}
    </div>
  )
}

function SectionHeader({ title, sub, onAdd, addLabel }: { title: string; sub: string; onAdd?: () => void; addLabel?: string }) {
  return (
    <div className="flex items-end justify-between gap-4">
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="text-xs text-muted-foreground">{sub}</p>
      </div>
      {onAdd && (
        <Button size="sm" variant="outline" onClick={onAdd}>
          <Plus className="h-4 w-4" /> {addLabel}
        </Button>
      )}
    </div>
  )
}

function Table({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50 text-left text-xs text-muted-foreground border-border">
            {head.map((h, i) => <th key={i} className="px-3 py-2 font-medium">{h}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

function EmptyRow({ cols, text }: { cols: number; text: string }) {
  return <tr><td colSpan={cols} className="px-3 py-6 text-center text-sm text-muted-foreground">{text}</td></tr>
}

function RowActions({ onEdit, onDelete }: { onEdit(): void; onDelete(): void }) {
  return (
    <td className="px-2 py-1 text-right">
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onEdit} aria-label="Editar"><Pencil className="h-3.5 w-3.5" /></Button>
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onDelete} aria-label="Apagar"><Trash2 className="h-3.5 w-3.5" /></Button>
    </td>
  )
}

function useSave(onClose: () => void) {
  const { shell, reload } = useProducao()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const run = async (fn: (s: NonNullable<typeof shell>) => Promise<unknown>) => {
    if (!shell) return
    setSaving(true)
    setError(null)
    try {
      await fn(shell)
      await reload()
      onClose()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setSaving(false)
    }
  }
  return { saving, error, run }
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  )
}

/** Equipas do Koortex (para ligar a carrinha à sua, D2). Sem `teams.read`, vazio. */
function useKoortexTeams(enabled: boolean): KoortexTeam[] {
  const { shell } = useProducao()
  const [teams, setTeams] = useState<KoortexTeam[]>([])
  useEffect(() => {
    if (!shell || !enabled) return
    let cancelled = false
    producaoApi.teams(shell).then((t) => !cancelled && setTeams(t)).catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [shell, enabled])
  return teams
}

/** `teams` null = modo local: sem campo de equipa e sem `team_id` no pedido (a coluna pode nem existir). */
function VanDialog({ van, technicians, labels, teams, onClose }: { van: Partial<Van>; technicians: Technician[]; labels: TrelloOption[]; teams: KoortexTeam[] | null; onClose(): void }) {
  const [v, setV] = useState(van)
  const { saving, error, run } = useSave(onClose)
  const body = {
    name: v.name ?? "",
    odoo_location: v.odoo_location || null,
    technician_id: v.technician_id || null,
    color: v.color ?? TRELLO_COLORS[5]!.hex,
    trello_label_id: v.trello_label_id || null,
    ...(teams ? { team_id: v.team_id != null && String(v.team_id) !== "" ? Number(v.team_id) : null } : {}),
    active: v.active !== false,
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{van.id ? "Editar carrinha" : "Nova carrinha"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Field label="Nome" htmlFor="van-name"><Input id="van-name" value={v.name ?? ""} onChange={(e) => setV({ ...v, name: e.target.value })} placeholder="Carrinha 1" /></Field>
          <Field label="Armazém no Odoo" htmlFor="van-loc"><Input id="van-loc" value={v.odoo_location ?? ""} onChange={(e) => setV({ ...v, odoo_location: e.target.value })} placeholder="CAR-01" /></Field>
          <Field label="Chefe fixo" htmlFor="van-lead">
            <select id="van-lead" className={selectClass} value={v.technician_id ?? ""} onChange={(e) => setV({ ...v, technician_id: e.target.value || null })}>
              <option value="">—</option>
              {technicians.filter((t) => t.active && t.kind !== "armazem").map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_lead ? " (chefe)" : ""}</option>)}
            </select>
          </Field>
          {teams && <Field label="Equipa no Koortex" htmlFor="van-team">
            <select id="van-team" className={selectClass} value={v.team_id != null ? String(v.team_id) : ""} onChange={(e) => setV({ ...v, team_id: e.target.value ? Number(e.target.value) : null })}>
              <option value="">—</option>
              {teams.filter((t) => t.is_active || Number(v.team_id) === t.id).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>}
          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium text-foreground/80">Cor da equipa</legend>
            <div className="flex flex-wrap gap-2">
              {TRELLO_COLORS.map((c) => (
                <button key={c.hex} type="button" title={c.name} aria-label={c.name} aria-pressed={v.color === c.hex} onClick={() => setV({ ...v, color: c.hex })}
                  className={cn("h-7 w-7 rounded-full border-2 border-border", v.color === c.hex ? "border-foreground" : "border-transparent")} style={{ backgroundColor: c.hex }} />
              ))}
            </div>
          </fieldset>
          <Field label="Etiqueta no Trello" htmlFor="van-label">
            {labels.length > 0 ? (
              <select id="van-label" className={selectClass} value={v.trello_label_id ?? ""} onChange={(e) => setV({ ...v, trello_label_id: e.target.value || null })}>
                <option value="">—</option>
                {labels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            ) : (
              <Input id="van-label" value={v.trello_label_id ?? ""} onChange={(e) => setV({ ...v, trello_label_id: e.target.value })} placeholder="Id da etiqueta (configure o quadro em Configurações para escolher)" />
            )}
          </Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v.active !== false} onChange={(e) => setV({ ...v, active: e.target.checked })} /> Ativa</label>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={saving || !body.name.trim()} onClick={() => run((s) => (van.id ? producaoApi.updateVan(s, van.id, body) : producaoApi.createVan(s, body)))}>Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** O papel na tabela: técnico, chefe de equipa, armazém (e o responsável). */
function roleLabel(t: Pick<Technician, "is_lead" | "kind" | "can_manage_stock">): string {
  if (t.kind === "armazem") return t.can_manage_stock ? "Armazém · responsável" : "Armazém"
  return t.is_lead ? "Chefe de equipa" : "Técnico"
}

function TechDialog({ tech, vans, members, onClose }: { tech: Partial<Technician>; vans: Van[]; members: TrelloOption[]; onClose(): void }) {
  const [t, setT] = useState(tech)
  const { saving, error, run } = useSave(onClose)
  // Um papel por pessoa (E3): o armazém não tem carrinha, obras nem chefia de equipa.
  const armazem = t.kind === "armazem"
  const body = {
    name: t.name ?? "",
    initials: t.initials || null,
    kind: armazem ? ("armazem" as const) : ("tecnico" as const),
    can_manage_stock: armazem && t.can_manage_stock === true,
    is_lead: !armazem && t.is_lead === true,
    van_id: armazem ? null : t.van_id || null,
    trello_member_id: armazem ? null : t.trello_member_id || null,
    active: t.active !== false,
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{tech.id ? "Editar pessoa" : "Nova pessoa"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Field label="Nome" htmlFor="t-name"><Input id="t-name" value={t.name ?? ""} onChange={(e) => setT({ ...t, name: e.target.value })} /></Field>
          <Field label="Iniciais (opcional)" htmlFor="t-ini"><Input id="t-ini" maxLength={4} value={t.initials ?? ""} onChange={(e) => setT({ ...t, initials: e.target.value })} /></Field>
          <Field label="Papel" htmlFor="t-kind">
            <select id="t-kind" className={selectClass} value={armazem ? "armazem" : "tecnico"} onChange={(e) => setT({ ...t, kind: e.target.value === "armazem" ? "armazem" : "tecnico" })}>
              <option value="tecnico">Técnico (carrinha e obras)</option>
              <option value="armazem">Armazém (Ronda de stock no telemóvel)</option>
            </select>
          </Field>
          {armazem ? (
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-0.5" checked={t.can_manage_stock === true} onChange={(e) => setT({ ...t, can_manage_stock: e.target.checked })} />
              <span>
                Responsável do armazém
                <span className="block text-xs text-muted-foreground">Faz rondas «Tudo da localização», fecha rondas com pendentes e desiste de um envio a meio — no telemóvel.</span>
              </span>
            </label>
          ) : (
          <>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={t.is_lead === true} onChange={(e) => setT({ ...t, is_lead: e.target.checked })} /> Chefe de equipa</label>
          <Field label="Carrinha habitual" htmlFor="t-van">
            <select id="t-van" className={selectClass} value={t.van_id ?? ""} onChange={(e) => setT({ ...t, van_id: e.target.value || null })}>
              <option value="">—</option>
              {vans.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </Field>
          <Field label="Membro no Trello" htmlFor="t-member">
            {members.length > 0 ? (
              <select id="t-member" className={selectClass} value={t.trello_member_id ?? ""} onChange={(e) => setT({ ...t, trello_member_id: e.target.value || null })}>
                <option value="">—</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            ) : (
              <Input id="t-member" value={t.trello_member_id ?? ""} onChange={(e) => setT({ ...t, trello_member_id: e.target.value })} placeholder="Id do membro (configura o quadro abaixo para escolher)" />
            )}
          </Field>
          </>
          )}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={t.active !== false} onChange={(e) => setT({ ...t, active: e.target.checked })} /> Ativo</label>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={saving || !body.name.trim()} onClick={() => run((s) => (tech.id ? producaoApi.updateTechnician(s, tech.id, body) : producaoApi.createTechnician(s, body)))}>Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Absences({ absences, technicians }: { absences: Absence[]; technicians: Technician[] }) {
  const { shell, reload } = useProducao()
  const [form, setForm] = useState({ technician_id: "", date_from: todayIso(), date_to: todayIso(), reason: "Férias" })
  const [error, setError] = useState<string | null>(null)
  const today = todayIso()
  const current = absences.filter((a) => a.date_to >= addDays(today, -30)).sort((a, b) => a.date_from.localeCompare(b.date_from))

  const add = async () => {
    if (!shell) return
    try {
      await producaoApi.createAbsence(shell, { ...form, reason: form.reason || null })
      setError(null)
      await reload()
    } catch (err) {
      setError(errorMessage(err))
    }
  }
  const del = async (id: string) => {
    if (!shell) return
    await producaoApi.deleteAbsence(shell, id).catch(() => undefined)
    await reload()
  }

  return (
    <section className="space-y-3">
      <SectionHeader title="Ausências" sub="Férias, baixas e formação. O painel de atribuição usa-as para a disponibilidade." />
      <div className="flex flex-wrap items-end gap-2 rounded-xl border p-3 border-border">
        <div className="w-52 space-y-1">
          <Label htmlFor="ab-t">Técnico</Label>
          <select id="ab-t" className={selectClass} value={form.technician_id} onChange={(e) => setForm({ ...form, technician_id: e.target.value })}>
            <option value="">—</option>
            {technicians.filter((t) => t.active).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="space-y-1"><Label htmlFor="ab-from">De</Label><Input id="ab-from" type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} /></div>
        <div className="space-y-1"><Label htmlFor="ab-to">Até</Label><Input id="ab-to" type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} /></div>
        <div className="w-40 space-y-1">
          <Label htmlFor="ab-r">Motivo</Label>
          <select id="ab-r" className={selectClass} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}>
            {["Férias", "Baixa", "Formação", "Outro"].map((r) => <option key={r}>{r}</option>)}
          </select>
        </div>
        <Button size="sm" onClick={add} disabled={!form.technician_id}><Plus className="h-4 w-4" /> Adicionar</Button>
        {error && <p className="w-full text-sm text-destructive">{error}</p>}
      </div>
      <ul className="divide-y divide-border rounded-xl border border-border">
        {current.map((a) => (
          <li key={a.id} className="flex items-center justify-between px-3 py-2 text-sm">
            <span>
              <span className="font-medium">{technicians.find((t) => t.id === a.technician_id)?.name ?? "—"}</span>
              <span className="text-muted-foreground"> · {a.reason ?? "Ausente"} · {fmtShort(a.date_from)}{a.date_to !== a.date_from && ` – ${fmtShort(a.date_to)}`}</span>
            </span>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => del(a.id)} aria-label="Apagar ausência"><Trash2 className="h-3.5 w-3.5" /></Button>
          </li>
        ))}
        {current.length === 0 && <li className="px-3 py-4 text-center text-sm text-muted-foreground">Sem ausências.</li>}
      </ul>
    </section>
  )
}
